import { Column, FormContext, Row } from '@/components/SettingsLayout';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.css';
import { Text, Icon, IconType } from '@/ui';
import PlanRestrictions from '@/pages/accounts/components/plans/PlanRestrictions';
import { useGetAllFeatureGroupsWithFeaturesQuery } from '@/store/slices/featuresApiSlice';
import { useGetAllExtensionsQuery } from '@/store/slices/extensionsApiSlice';
import CompanySkeleton from './CompanySkeleton';
import {
  createPlanData,
  getDefaultPlanData,
  transformFormDataToFeatures,
} from '@/pages/accounts/helpers/utils';
import { useEffect, useState } from 'react';
import { getDefaultDataFromPlan } from '@/pages/accounts/helpers/utils';
import { Plan } from '@/services/Plans/plans.service';
import { useGetUser } from '@/hooks';
import { useCreateOrUpdateCustomPlanMutation } from '@/store';
import toast from 'react-hot-toast';
import deepEqual from '@/utils/helpers/deepEqual';
import { useParams } from 'react-router-dom';
import { Company } from '@/context/types';

function CompanyPlan({ plan, company }: { plan: Plan; company: Company }) {
  const { t } = useTranslation();
  const { user, currentUser } = useGetUser();
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<Record<string, any>>({});

  const { data: allFeatureGroupsWithFeatures, isLoading: featuresLoader } =
    useGetAllFeatureGroupsWithFeaturesQuery(user?.targetOrganizationId || '');
  const { data: allExtensions, isLoading: extensionsLoader } = useGetAllExtensionsQuery({
    organizationId: user?.targetOrganizationId || '',
  });

  const [createOrUpdateCustomPlan, { isLoading: isCreatingOrUpdateCustomPlan }] =
    useCreateOrUpdateCustomPlanMutation();

  useEffect(() => {
    if (!allFeatureGroupsWithFeatures) return;
    if (!allExtensions) return;
    let newDefaultData = {};
    if (plan) {
      newDefaultData = getDefaultDataFromPlan(plan);
    } else {
      newDefaultData = getDefaultPlanData(allFeatureGroupsWithFeatures);
    }

    setDefaultValues(newDefaultData);
  }, [allFeatureGroupsWithFeatures, allExtensions, plan]);

  if (extensionsLoader || featuresLoader) {
    return <CompanySkeleton />;
  }

  const onSubmit = (data: any) => {
    const hasChanges = !deepEqual(data, defaultValues);
    if (!hasChanges) {
      toast.error('No changes detected');
      return;
    }
    const featureGroupNames = (allFeatureGroupsWithFeatures ?? []).map((group) => group.name);
    const features = transformFormDataToFeatures(data, featureGroupNames);

    if (company) {
      data.name = t('Plans.custom_plan_name', { companyName: company.name });
    }
    const planData = createPlanData(
      data,
      user?.targetOrganizationId || currentUser?.companyId,
      features,
    );
    createOrUpdateCustomPlan({
      planData,
      organizationId: currentUser?.companyId || '',
      companyId: id || '',
    })
      .unwrap()
      .then(() => {
        toast.success(t('Messages.plan_update_success'));
      })
      .catch(() => {
        toast.error(t('Messages.plan_update_error'));
      });
  };

  return (
    <FormContext submitHandler={onSubmit} defaultValues={defaultValues} resetContext={true}>
      <div className={styles.companyPlanContainer}>
        <Column gapSize='24'>
          <div className={styles.warningDiv}>
            <Row gapSize='8' align='start'>
              <div className={styles.coralSvg}>
                <Icon type={IconType.Info2} width={24} height={24} fill='transparent' />
              </div>
              <Text size='s' weight='light-medium'>
                {t('Accounts.plan_warning')}
              </Text>
            </Row>
          </div>
          <PlanRestrictions
            featureGroup={allFeatureGroupsWithFeatures || []}
            extensions={allExtensions || []}
            isSaving={isCreatingOrUpdateCustomPlan}
          />
        </Column>
      </div>
    </FormContext>
  );
}

export default CompanyPlan;
